import React, { useState, useEffect, useRef } from 'react';
import { authService } from '../services/auth.service';
import api from '../services/api';
import InputNombre from '../components/InputNombre';
import { THEMES, themeService } from '../services/theme.service';
import { logoService } from '../services/logo.service';
import { entrepriseService } from '../services/entreprise.service';
import './Parametres.css';

export default function Parametres() {
  const user = authService.getUser();
  const [onglet, setOnglet] = useState<'entreprise' | 'tarification' | 'facturation' | 'notifications' | 'apparence'>('entreprise');
  const [themeActuel, setThemeActuel] = useState(themeService.getThemeActuel());
  const [logo, setLogo] = useState<string | null>(logoService.getLogo());
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { setErreur('Logo trop lourd (max 500 Ko)'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      try {
        await logoService.setLogo(base64);
        setLogo(base64);
        setSucces('Logo mis à jour (partagé avec tous les postes)');
        setTimeout(() => setSucces(''), 3000);
      } catch {
        setErreur("Échec de l'enregistrement du logo sur le serveur");
        setTimeout(() => setErreur(''), 3000);
      }
    };
    reader.readAsDataURL(file);
  };

  const supprimerLogo = async () => {
    try {
      await logoService.supprimer();
      setLogo(null);
      if (logoInputRef.current) logoInputRef.current.value = '';
    } catch {
      setErreur('Échec de la suppression du logo');
      setTimeout(() => setErreur(''), 3000);
    }
  };
  const [succes, setSucces] = useState('');
  const [erreur, setErreur] = useState('');

  const [entreprise, setEntreprise] = useState({
    nom: '',
    ifu: '',
    rccm: '',
    telephone: '',
    email: '',
    adresse: '',
    ville: '',
    siteWeb: '',
    cabinetComptable: '',
    emailCabinet: '',
  });

  const [tarification, setTarification] = useState({
    remise_revendeur: 15,
    remise_technicien: 22,
    tva_taux: 18,
    points_par_1000: 1,
  });

  const [facturation, setFacturation] = useState({
    prefixeFacture: 'FACT',
    tvaDefaut: true,
    tauxTva: 18,
    mentionLegale: 'Facture émise conformément aux dispositions du Code Général des Impôts du Burkina Faso.',
    piedPage: 'Merci pour votre confiance !',
  });

  const [notifications, setNotifications] = useState({
    alerteRupture: true,
    alerteStockFaible: true,
    exportAutoMensuel: false,
  });

  useEffect(() => {
    chargerEntreprise();
    chargerConfigurations();
    logoService.synchroniser().then(setLogo);
  }, []);

  // Charge la fiche de l'entreprise de l'utilisateur connecté.
  const chargerEntreprise = async () => {
    try {
      const res = await api.get('/entreprises/moi');
      const e = res.data;
      setEntreprise(prev => ({
        ...prev,
        nom: e.nom || '',
        ifu: e.ifu || '',
        rccm: e.rccm || '',
        telephone: e.telephone || '',
        email: e.email || '',
        adresse: e.adresse || '',
        ville: e.ville || '',
        siteWeb: e.siteWeb || '',
      }));
    } catch (e) {}
  };

  useEffect(() => {
  }, [onglet]);

  const chargerConfigurations = async () => {
    try {
      const res = await api.get('/configurations');
      const config = res.data;
      setTarification({
        remise_revendeur: parseFloat(config.remise_revendeur || '15'),
        remise_technicien: parseFloat(config.remise_technicien || '22'),
        tva_taux: parseFloat(config.tva_taux || '18'),
        points_par_1000: parseFloat(config.points_par_1000 || '1'),
      });
      setFacturation(f => ({
        ...f,
        tauxTva: parseFloat(config.tva_taux || '18'),
        prefixeFacture: config.prefixe_facture ?? f.prefixeFacture,
        mentionLegale: config.mention_legale ?? f.mentionLegale,
        piedPage: config.pied_page ?? f.piedPage,
        tvaDefaut: config.tva_defaut != null ? config.tva_defaut === 'true' : f.tvaDefaut,
      }));
      setNotifications(n => ({
        alerteRupture: config.alerte_rupture != null ? config.alerte_rupture === 'true' : n.alerteRupture,
        alerteStockFaible: config.alerte_stock_faible != null ? config.alerte_stock_faible === 'true' : n.alerteStockFaible,
        exportAutoMensuel: config.export_auto_mensuel != null ? config.export_auto_mensuel === 'true' : n.exportAutoMensuel,
      }));
      setEntreprise(prev => ({
        ...prev,
        cabinetComptable: config.cabinet_comptable ?? prev.cabinetComptable,
        emailCabinet: config.email_cabinet ?? prev.emailCabinet,
      }));
    } catch (e) {}
  };

  const sauvegarderTarification = async () => {
    try {
      await api.put('/configurations', {
        remise_revendeur: String(tarification.remise_revendeur),
        remise_technicien: String(tarification.remise_technicien),
        tva_taux: String(tarification.tva_taux),
        points_par_1000: String(tarification.points_par_1000),
      });
      setSucces('Tarification mise à jour avec succès');
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la sauvegarde');
      setTimeout(() => setErreur(''), 3000);
    }
  };

  // Sauvegarde réelle des onglets Entreprise / Facturation / Notifications :
  // - infos de l'entreprise -> PUT /entreprises/moi (liste blanche côté serveur)
  // - autres champs -> magasin de configurations clé/valeur (par entreprise)
  const sauvegarder = async () => {
    try {
      await api.put('/entreprises/moi', {
        nom: entreprise.nom,
        adresse: entreprise.adresse,
        ville: entreprise.ville,
        telephone: entreprise.telephone,
        email: entreprise.email,
        ifu: entreprise.ifu,
        rccm: entreprise.rccm,
        siteWeb: entreprise.siteWeb,
        piedPage: facturation.piedPage,
      });
      await api.put('/configurations', {
        cabinet_comptable: entreprise.cabinetComptable,
        email_cabinet: entreprise.emailCabinet,
        prefixe_facture: facturation.prefixeFacture,
        mention_legale: facturation.mentionLegale,
        pied_page: facturation.piedPage,
        tva_defaut: String(facturation.tvaDefaut),
        alerte_rupture: String(notifications.alerteRupture),
        alerte_stock_faible: String(notifications.alerteStockFaible),
        export_auto_mensuel: String(notifications.exportAutoMensuel),
      });
      // Les tickets/factures rechargeront les nouvelles infos d'en-tête.
      entrepriseService.invaliderCache();
      setSucces('Paramètres sauvegardés avec succès');
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setErreur(typeof msg === 'string' ? msg : 'Erreur lors de la sauvegarde des paramètres');
      setTimeout(() => setErreur(''), 4000);
    }
  };

  const prixAvecRemise = (prix: number, remise: number) =>
    Math.round(prix * (1 - remise / 100)).toLocaleString('fr-FR');

  return (
    <div className="parametres-page">
      <div className="page-header">
        <div>
          <h1>Paramètres</h1>
          <p>Configuration générale de NexaCom</p>
        </div>
        {onglet !== 'tarification' && onglet !== 'apparence' && (
          <button className="btn-primary" onClick={sauvegarder}>💾 Sauvegarder</button>
        )}
        {onglet === 'tarification' && (
          <button className="btn-primary" onClick={sauvegarderTarification}>💾 Appliquer</button>
        )}
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      <div className="param-tabs">
        <button className={onglet === 'entreprise' ? 'active' : ''}
          onClick={() => setOnglet('entreprise')}>🏢 Entreprise</button>
        <button className={onglet === 'tarification' ? 'active' : ''}
          onClick={() => setOnglet('tarification')}>💲 Tarification</button>
        <button className={onglet === 'facturation' ? 'active' : ''}
          onClick={() => setOnglet('facturation')}>🧾 Facturation</button>
        <button className={onglet === 'notifications' ? 'active' : ''}
          onClick={() => setOnglet('notifications')}>🔔 Notifications</button>
        <button className={onglet === 'apparence' ? 'active' : ''}
          onClick={() => setOnglet('apparence')}>🎨 Apparence</button>
      </div>

      {/* ENTREPRISE */}
      {onglet === 'entreprise' && (
        <div className="param-section">
          <div className="param-card">
            <div className="param-card-titre">Informations de l'entreprise</div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Nom de l'entreprise</label>
                <input value={entreprise.nom}
                  onChange={e => setEntreprise(f => ({ ...f, nom: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Site web</label>
                <input value={entreprise.siteWeb}
                  onChange={e => setEntreprise(f => ({ ...f, siteWeb: e.target.value }))}
                  placeholder="www.monentreprise.bf" />
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>IFU</label>
                <input value={entreprise.ifu}
                  onChange={e => setEntreprise(f => ({ ...f, ifu: e.target.value }))}
                  style={{ fontFamily: 'monospace' }} />
              </div>
              <div className="form-group">
                <label>RCCM</label>
                <input value={entreprise.rccm}
                  onChange={e => setEntreprise(f => ({ ...f, rccm: e.target.value }))}
                  style={{ fontFamily: 'monospace' }} />
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Téléphone</label>
                <input value={entreprise.telephone}
                  onChange={e => setEntreprise(f => ({ ...f, telephone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={entreprise.email}
                  onChange={e => setEntreprise(f => ({ ...f, email: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label>Adresse</label>
              <input value={entreprise.adresse}
                onChange={e => setEntreprise(f => ({ ...f, adresse: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Ville</label>
              <select value={entreprise.ville}
                onChange={e => setEntreprise(f => ({ ...f, ville: e.target.value }))}>
                <option>Ouagadougou</option>
                <option>Bobo-Dioulasso</option>
                <option>Koudougou</option>
                <option>Banfora</option>
                <option>Ouahigouya</option>
              </select>
            </div>
          </div>
          <div className="param-card">
            <div className="param-card-titre">Cabinet comptable externe</div>
            <div className="param-card-desc">Les exports seront envoyés à votre cabinet.</div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Nom du cabinet</label>
                <input value={entreprise.cabinetComptable}
                  onChange={e => setEntreprise(f => ({ ...f, cabinetComptable: e.target.value }))}
                  placeholder="Ex: Cabinet Ouaga Conseils" />
              </div>
              <div className="form-group">
                <label>Email du cabinet</label>
                <input type="email" value={entreprise.emailCabinet}
                  onChange={e => setEntreprise(f => ({ ...f, emailCabinet: e.target.value }))}
                  placeholder="comptable@cabinet.bf" />
              </div>
            </div>
          </div>
          <div className="param-card">
            <div className="param-card-titre">Logo de l'entreprise</div>
            <div className="param-card-desc">Affiché sur les tickets et factures imprimés.</div>
            <div className="logo-upload-zone">
              {logo ? (
                <div className="logo-apercu">
                  <img src={logo} alt="Logo" className="logo-img" />
                  <div className="logo-actions">
                    <button className="btn-secondary" onClick={() => logoInputRef.current?.click()}>
                      🔄 Changer
                    </button>
                    <button className="btn-danger-outline" onClick={supprimerLogo}>
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ) : (
                <div className="logo-vide" onClick={() => logoInputRef.current?.click()}>
                  <div className="logo-vide-icone">🖼️</div>
                  <div className="logo-vide-texte">Cliquer pour ajouter un logo</div>
                  <div className="logo-vide-hint">PNG, JPG — max 500 Ko</div>
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/svg+xml"
                style={{ display: 'none' }}
                onChange={handleLogoUpload}
              />
            </div>
          </div>

          <div className="param-card">
            <div className="param-card-titre">Compte connecté</div>
            <div className="compte-info">
              <div className="compte-avatar">{user?.prenom?.[0]}{user?.nom?.[0]}</div>
              <div>
                <div className="compte-nom">{user?.prenom} {user?.nom}</div>
                <div className="compte-email">{user?.email}</div>
                <div className="compte-role">{user?.role}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TARIFICATION */}
      {onglet === 'tarification' && (
        <div className="param-section">
          <div className="param-card">
            <div className="param-card-titre">💲 Remises par type de client</div>
            <div className="param-card-desc">
              Ces remises s'appliquent automatiquement à la caisse selon le type de client sélectionné.
              Elles sont également utilisées pour calculer les prix Revendeur et Technicien lors de la création d'un produit.
            </div>

            <div className="tarif-grid">
              {/* ORDINAIRE */}
              <div className="tarif-card ordinaire">
                <div className="tarif-icon">👤</div>
                <div className="tarif-type">Client Ordinaire</div>
                <div className="tarif-desc">Prix public standard — aucune remise</div>
                <div className="tarif-remise-val">0%</div>
                <div className="tarif-exemple">
                  Ex: 10 000 FCFA → <strong>10 000 FCFA</strong>
                </div>
              </div>

              {/* REVENDEUR */}
              <div className="tarif-card revendeur">
                <div className="tarif-icon">🏪</div>
                <div className="tarif-type">Client Revendeur</div>
                <div className="tarif-desc">Remise accordée aux revendeurs professionnels</div>
                <div className="tarif-input-group">
                  <InputNombre
                    value={tarification.remise_revendeur}
                    onChange={val => setTarification(t => ({ ...t, remise_revendeur: Math.min(50, Math.max(0, val)) }))}
                    placeholder="15"
                    min={0}
                    max={50}
                  />
                  <span className="tarif-pct">%</span>
                </div>
                <div className="tarif-exemple">
                  Ex: 10 000 FCFA → <strong>{prixAvecRemise(10000, tarification.remise_revendeur)} FCFA</strong>
                </div>
                <div className="tarif-barre-wrap">
                  <div className="tarif-barre revendeur"
                    style={{ width: `${tarification.remise_revendeur * 2}%` }} />
                </div>
              </div>

              {/* TECHNICIEN */}
              <div className="tarif-card technicien">
                <div className="tarif-icon">🔧</div>
                <div className="tarif-type">Client Technicien</div>
                <div className="tarif-desc">Remise accordée aux techniciens agréés</div>
                <div className="tarif-input-group">
                  <InputNombre
                    value={tarification.remise_technicien}
                    onChange={val => setTarification(t => ({ ...t, remise_technicien: Math.min(50, Math.max(0, val)) }))}
                    placeholder="22"
                    min={0}
                    max={50}
                  />
                  <span className="tarif-pct">%</span>
                </div>
                <div className="tarif-exemple">
                  Ex: 10 000 FCFA → <strong>{prixAvecRemise(10000, tarification.remise_technicien)} FCFA</strong>
                </div>
                <div className="tarif-barre-wrap">
                  <div className="tarif-barre technicien"
                    style={{ width: `${tarification.remise_technicien * 2}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div className="param-card">
            <div className="param-card-titre">🏦 TVA & Fiscalité</div>
            <div className="tarif-fiscal">
              <div className="fiscal-row">
                <div>
                  <div className="fiscal-label">Taux de TVA</div>
                  <div className="fiscal-desc">Taux appliqué sur les ventes soumises à la TVA</div>
                </div>
                <div className="tarif-input-group">
                  <InputNombre
                    value={tarification.tva_taux}
                    onChange={val => setTarification(t => ({ ...t, tva_taux: Math.min(30, Math.max(0, val)) }))}
                    placeholder="18"
                    min={0}
                    max={30}
                  />
                  <span className="tarif-pct">%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="param-card">
            <div className="param-card-titre">⭐ Programme de fidélité</div>
            <div className="tarif-fiscal">
              <div className="fiscal-row">
                <div>
                  <div className="fiscal-label">Points par 1 000 FCFA dépensés</div>
                  <div className="fiscal-desc">Nombre de points attribués pour chaque tranche de 1 000 FCFA achetés</div>
                </div>
                <div className="tarif-input-group">
                  <InputNombre
                    value={tarification.points_par_1000}
                    onChange={val => setTarification(t => ({ ...t, points_par_1000: Math.max(1, val) }))}
                    placeholder="1"
                    min={1}
                  />
                  <span className="tarif-pct" style={{ width: 'auto', padding: '0 8px' }}>pts</span>
                </div>
              </div>
              <div className="fidelite-niveaux">
                <div className="fn-titre">Niveaux de fidélité</div>
                <div className="fn-grid">
                  <div className="fn-card bronze">
                    <span>🥉 Bronze</span>
                    <span>0 — 4 999 pts</span>
                  </div>
                  <div className="fn-card argent">
                    <span>🥈 Argent</span>
                    <span>5 000 — 14 999 pts</span>
                  </div>
                  <div className="fn-card or">
                    <span>⭐ Or</span>
                    <span>15 000+ pts</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="param-card info-tarif">
            <div className="param-card-titre">ℹ️ Comment fonctionnent les remises</div>
            <div className="tarif-info-liste">
              <div className="til-item">
                <span>🛒</span>
                <span>À la <strong>caisse</strong>, le prix s'ajuste automatiquement quand vous sélectionnez le type de client</span>
              </div>
              <div className="til-item">
                <span>📦</span>
                <span>Lors de la <strong>création d'un produit</strong>, les prix Revendeur et Technicien sont calculés automatiquement à partir du prix public</span>
              </div>
              <div className="til-item">
                <span>⚠️</span>
                <span>Modifier les remises ici ne met <strong>pas à jour</strong> les prix déjà enregistrés sur les produits existants — seuls les nouveaux calculs seront affectés</span>
              </div>
              <div className="til-item">
                <span>🔒</span>
                <span>La remise maximale est limitée à <strong>50%</strong> pour éviter les erreurs de saisie</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FACTURATION */}
      {onglet === 'facturation' && (
        <div className="param-section">
          <div className="param-card">
            <div className="param-card-titre">Configuration des factures</div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Préfixe des factures</label>
                <input value={facturation.prefixeFacture}
                  onChange={e => setFacturation(f => ({ ...f, prefixeFacture: e.target.value }))}
                  style={{ fontFamily: 'monospace' }} />
                <div className="form-hint">Exemple : {facturation.prefixeFacture}-2026-0001</div>
              </div>
              <div className="form-group">
                <label>Taux TVA (%)</label>
                <input type="number" value={facturation.tauxTva}
                  onChange={e => setFacturation(f => ({ ...f, tauxTva: +e.target.value }))}
                  min="0" max="100" />
              </div>
            </div>
            <div className="form-group">
              <label className="toggle-row">
                <span>TVA activée par défaut à la caisse</span>
                <input type="checkbox" checked={facturation.tvaDefaut}
                  onChange={e => setFacturation(f => ({ ...f, tvaDefaut: e.target.checked }))} />
              </label>
            </div>
          </div>
          <div className="param-card">
            <div className="param-card-titre">Mentions légales sur les factures</div>
            <div className="form-group">
              <label>Mention légale</label>
              <textarea value={facturation.mentionLegale}
                onChange={e => setFacturation(f => ({ ...f, mentionLegale: e.target.value }))}
                rows={3}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 7, fontFamily: 'inherit', fontSize: 13 }} />
            </div>
            <div className="form-group">
              <label>Pied de page</label>
              <input value={facturation.piedPage}
                onChange={e => setFacturation(f => ({ ...f, piedPage: e.target.value }))}
                placeholder="Ex: Merci pour votre confiance !" />
            </div>
          </div>
          <div className="param-card apercu">
            <div className="param-card-titre">Aperçu d'une facture</div>
            <div className="facture-apercu">
              <div className="fa-header">
                <div className="fa-logo">{entreprise.nom}</div>
                <div className="fa-info">
                  <div>IFU : {entreprise.ifu}</div>
                  <div>RCCM : {entreprise.rccm}</div>
                  <div>{entreprise.adresse}</div>
                </div>
              </div>
              <div className="fa-num">N° {facturation.prefixeFacture}-2026-0001</div>
              <div className="fa-mention">{facturation.mentionLegale}</div>
              <div className="fa-pied">{facturation.piedPage}</div>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATIONS */}
      {onglet === 'notifications' && (
        <div className="param-section">
          <div className="param-card">
            <div className="param-card-titre">Alertes stock</div>
            <div className="notif-liste">
              <label className="toggle-row">
                <div>
                  <div className="toggle-label">⚠️ Alerte rupture de stock</div>
                  <div className="toggle-desc">Notification quand un produit tombe à 0 unité</div>
                </div>
                <input type="checkbox" checked={notifications.alerteRupture}
                  onChange={e => setNotifications(f => ({ ...f, alerteRupture: e.target.checked }))} />
              </label>
              <label className="toggle-row">
                <div>
                  <div className="toggle-label">📉 Alerte stock faible</div>
                  <div className="toggle-desc">Notification quand un produit passe sous le stock minimum</div>
                </div>
                <input type="checkbox" checked={notifications.alerteStockFaible}
                  onChange={e => setNotifications(f => ({ ...f, alerteStockFaible: e.target.checked }))} />
              </label>
            </div>
          </div>
          <div className="param-card">
            <div className="param-card-titre">Informations système</div>
            <div className="sys-info">
              <div className="sys-row"><span>Version NexaCom</span><span className="sys-val">1.0.0</span></div>
              <div className="sys-row"><span>Base de données</span><span className="sys-val" style={{ color: '#3B6D11' }}>✓ Connectée</span></div>
              <div className="sys-row"><span>Environnement</span><span className="sys-val">Développement local</span></div>
              <div className="sys-row">
                <span>Remise Revendeur</span>
                <span className="sys-val">{tarification.remise_revendeur}%</span>
              </div>
              <div className="sys-row">
                <span>Remise Technicien</span>
                <span className="sys-val">{tarification.remise_technicien}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPARENCE */}
      {onglet === 'apparence' && (
        <div className="param-section">
          <div className="param-card">
            <div className="param-card-titre">🎨 Thème de couleurs</div>
            <div className="param-card-desc">
              Choisissez un thème de couleurs pour personnaliser l'interface. Le changement est appliqué immédiatement.
            </div>
            <div className="theme-grid">
              {THEMES.map(theme => (
                <div
                  key={theme.id}
                  className={`theme-card ${themeActuel.id === theme.id ? 'active' : ''}`}
                  onClick={() => {
                    themeService.appliquer(theme);
                    setThemeActuel(theme);
                  }}
                >
                  <div className="theme-preview">
                    <div className="theme-cercle" style={{ background: theme.primary }} />
                    <div className="theme-cercle theme-cercle-sm" style={{ background: theme.primaryDark }} />
                    <div className="theme-cercle theme-cercle-sm" style={{ background: theme.bgApp, border: '1px solid #E2E8F0' }} />
                  </div>
                  <div className="theme-nom">{theme.nom}</div>
                  {themeActuel.id === theme.id && (
                    <div className="theme-coche">✓ Actif</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SAUVEGARDE */}
    </div>
  );
}